import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer'
import { companyAddressLines, companyRegistrationLine, type CompanySettings } from '../../../lib/companySettings'
import type { DeliveryNoteType } from '../types'
import { registerPdfLongWordBreaking } from '../../../lib/pdfWrap'

registerPdfLongWordBreaking()

// Z50.X (2nd rewrite): built to visually match the two real reference PDFs Gary supplied --
// docs/warehouse/samples/DELIVERY_NOTE_SAMPLE.pdf ("CSNU6018970 DELIVERY NOTE.pdf") and
// COLLECTION_NOTE_SAMPLE.pdf ("DPD COLLECTION NOTE.pdf") -- not the fancier, differently
// laid out TransportNotePDF.tsx / Inbound/NotePDF.tsx templates used elsewhere in this app.
// Deliberately its own component rather than a third variant bolted onto either of those,
// since the address-block column count, the goods-table chrome (plain black borders, no
// coloured header, no per-row striping), and the two-row authorisation block all differ.

const S = StyleSheet.create({
  page: { padding: '15mm', fontFamily: 'Helvetica', fontSize: 9, color: '#111' },

  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  logo: { width: 90, height: 'auto' },
  logoFallback: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: '#1a1a1a' },
  companyBlock: { alignItems: 'flex-end' },
  companyName: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 1 },
  companyLine: { fontSize: 8, color: '#222', marginBottom: 1 },

  titleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 },
  title: { fontSize: 22, fontFamily: 'Helvetica-Bold' },
  refsRow: { flexDirection: 'row', gap: 28 },
  refBlock: { alignItems: 'flex-start' },
  refLbl: { fontSize: 9, color: '#111', marginBottom: 2 },
  refVal: { fontSize: 9, color: '#111' },
  refValBold: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#111' },

  addrRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000', marginBottom: 10 },
  addrBox: { flex: 1, borderRightWidth: 1, borderColor: '#000', padding: '6 8', minHeight: 90 },
  addrBoxLast: { flex: 1, padding: '6 8', minHeight: 90 },
  addrBoxTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  addrHdr: { fontSize: 10, fontFamily: 'Helvetica-Bold' },
  addrHdrInline: { fontSize: 9, fontFamily: 'Helvetica-Bold', textAlign: 'right' },
  addrValInline: { fontSize: 10, fontFamily: 'Helvetica-Bold', textAlign: 'right', marginTop: 2 },
  addrLine: { fontSize: 9, marginTop: 2 },
  addrPhone: { fontSize: 9, marginTop: 8 },

  table: { borderWidth: 1, borderColor: '#000', marginBottom: 10 },
  thead: { flexDirection: 'row' },
  th: { fontSize: 9, fontFamily: 'Helvetica-Bold', padding: '4 6' },
  // Column of goods rows (each row is its own flex row so a wrapped Description keeps NOP/Weight/
  // Cube/Summary aligned on the same line); minHeight keeps the table's usual size when short.
  tbody: { borderTopWidth: 1, borderColor: '#000', minHeight: 130 },
  trowWrap: { flexDirection: 'row' },
  tcell: { borderRightWidth: 1, borderColor: '#000', padding: '4 6' },
  tcellLast: { padding: '4 6' },
  td: { fontSize: 9 },
  tdBold: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  sectionLbl: { fontSize: 9.5, fontFamily: 'Helvetica-Bold', textDecoration: 'underline', marginBottom: 3 },
  remarksBox: { borderWidth: 1, borderColor: '#000', minHeight: 46, padding: '4 6', marginBottom: 8 },
  remarksTxt: { fontSize: 9 },

  notesBox: { borderWidth: 1, borderColor: '#000', minHeight: 46, padding: '4 6', marginBottom: 10 },
  notesTxt: { fontSize: 9 },

  authBox: { borderWidth: 1, borderColor: '#000', marginBottom: 12 },
  authTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderBottomWidth: 1, borderColor: '#000', padding: '5 6' },
  authToTxt: { fontSize: 9, fontFamily: 'Helvetica-Bold', flex: 1 },
  authSerialTxt: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  authValsRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#000' },
  authValCell: { flex: 1, borderRightWidth: 1, borderColor: '#000', padding: '10 6 2 6' },
  authValCellLast: { flex: 1, padding: '10 6 2 6' },
  authValTxt: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  authLblsRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#000' },
  authLblCell: { flex: 1, borderRightWidth: 1, borderColor: '#000', padding: '2 6' },
  authLblCellLast: { flex: 1, padding: '2 6' },
  authLblTxt: { fontSize: 7.5, color: '#333' },
  authMidHdr: { fontSize: 9, fontFamily: 'Helvetica-Bold', padding: '5 6', borderBottomWidth: 1, borderColor: '#000' },

  footer: { position: 'absolute', bottom: '10mm', left: '15mm', right: '15mm', flexDirection: 'row', alignItems: 'center', gap: 8, borderTopWidth: 0.5, borderColor: '#999', paddingTop: 6 },
  bifaLogo: { width: 36, height: 'auto' },
  footerTxt: { fontSize: 6.5, color: '#444', lineHeight: 1.35, flex: 1, textAlign: 'center' },

  provisionalBadge: { position: 'absolute', top: '8mm', right: '15mm', borderWidth: 1.5, borderColor: '#dc2626', borderStyle: 'solid', paddingHorizontal: 6, paddingVertical: 3 },
  provisionalBadgeTxt: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#dc2626' },
})

export interface DCNAddressBox {
  label: string
  lines: string[]
  phone?: string | null
  // Collection Note's Collect-From box carries its own date top-right ("Collection Details").
  inlineLabel?: string
  inlineValue?: string | null
}

export interface DCNGoodsRow {
  marksNumbers: string
  description?: string | null
  nop?: number | null
  weight?: number | null
  cube?: number | null
  summary?: string | null
}

export interface DeliveryCollectionNoteData {
  company: CompanySettings
  noteType: DeliveryNoteType
  customerRef: string | null
  jobRef: string
  collectFrom: DCNAddressBox
  releaseTo: DCNAddressBox
  // Third box, only present for a Collection+Delivery job -- the final consignee.
  deliverTo: DCNAddressBox | null
  goods: DCNGoodsRow[]
  remarks: string | null
  notes: string | null
  authoriseTo: string | null
  serialNo: number | null // null for an unsaved Preview -- the real value only exists after insert
  issuedByName: string | null
  issuedAt: string // ISO
  // Z50.X: null/READY = no watermark. Anything else means the goods aren't confirmed in the
  // warehouse yet -- flagged so a driver can't show up with this PDF expecting to collect.
  readinessStatus?: 'PENDING_ARRIVAL' | 'PENDING_INBOUND' | 'READY' | null
}

export function fmtIssuedAt(iso: string) {
  const d = new Date(iso)
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-')
  return `${time} / ${date}`
}

function AddressBox({ box, bordered = true }: { box: DCNAddressBox; bordered?: boolean }) {
  return (
    <View style={bordered ? S.addrBox : S.addrBoxLast}>
      <View style={S.addrBoxTopRow}>
        <Text style={S.addrHdr}>{box.label}</Text>
        {box.inlineLabel && (
          <View>
            <Text style={S.addrHdrInline}>{box.inlineLabel}</Text>
            <Text style={S.addrValInline}>{box.inlineValue || '—'}</Text>
          </View>
        )}
      </View>
      {box.lines.map((l, i) => <Text key={i} style={S.addrLine}>{l}</Text>)}
      {box.phone && <Text style={S.addrPhone}>Telephone: {box.phone}</Text>}
    </View>
  )
}

export function DeliveryCollectionNotePDF({ note }: { note: DeliveryCollectionNoteData }) {
  const title = note.noteType === 'COLLECTION_SELF' ? 'Release/Delivery Note (Self-Pickup)'
    : note.noteType === 'COLLECTION_SUPPLIER' ? 'Release/Delivery Note (Supplier Pickup)'
    : 'Release/Delivery Note'

  return (
    <Document>
      <Page size="A4" style={S.page}>

        {note.readinessStatus && note.readinessStatus !== 'READY' && (
          <View style={S.provisionalBadge}>
            <Text style={S.provisionalBadgeTxt}>PROVISIONAL — GOODS NOT YET IN WAREHOUSE</Text>
          </View>
        )}

        {/* Letterhead -- logo top-left (falls back to a plain wordmark if company_settings
            has no logo_url yet), company block top-right, from company_settings only. */}
        <View style={S.header}>
          {note.company.logo_url
            ? <Image src={note.company.logo_url} style={S.logo} />
            : <Text style={S.logoFallback}>CY UK</Text>}
          <View style={S.companyBlock}>
            <Text style={S.companyName}>{note.company.legal_name}</Text>
            {companyAddressLines(note.company).map((l, i) => <Text key={i} style={S.companyLine}>{l}</Text>)}
            {note.company.phone && <Text style={S.companyLine}>T: {note.company.phone}</Text>}
          </View>
        </View>

        <View style={S.titleRow}>
          <Text style={S.title}>{title}</Text>
          <View style={S.refsRow}>
            <View style={S.refBlock}>
              <Text style={S.refLbl}>Customer Ref</Text>
              <Text style={S.refVal}>{note.customerRef || '—'}</Text>
            </View>
            <View style={S.refBlock}>
              <Text style={S.refLbl}>Job Reference</Text>
              <Text style={S.refValBold}>{note.jobRef}</Text>
            </View>
          </View>
        </View>

        {/* Address block -- 2 boxes (Collect From, w/ Collection Details date inline |
            Release To), plus a 3rd (Deliver To) when this is a Collection+Delivery job. */}
        <View style={S.addrRow}>
          <AddressBox box={note.collectFrom} />
          {note.deliverTo ? (
            <>
              <AddressBox box={note.releaseTo} />
              <AddressBox box={note.deliverTo} bordered={false} />
            </>
          ) : (
            <AddressBox box={note.releaseTo} bordered={false} />
          )}
        </View>

        {/* Marks & Numbers / Description / NOP / Weight / Cube / Summary */}
        <View style={S.table}>
          <View style={S.thead}>
            <Text style={[S.th, { width: 110, borderRightWidth: 1, borderColor: '#000' }]}>Marks &amp; Numbers</Text>
            <Text style={[S.th, { flex: 1, borderRightWidth: 1, borderColor: '#000' }]}>Description</Text>
            <Text style={[S.th, { width: 45, textAlign: 'right', borderRightWidth: 1, borderColor: '#000' }]}>NOP</Text>
            <Text style={[S.th, { width: 55, textAlign: 'right', borderRightWidth: 1, borderColor: '#000' }]}>Weight</Text>
            <Text style={[S.th, { width: 45, textAlign: 'right', borderRightWidth: 1, borderColor: '#000' }]}>Cube</Text>
            <Text style={[S.th, { width: 90 }]}>Summary</Text>
          </View>
          <View style={S.tbody}>
            {note.goods.map((g, i) => (
              <View key={i} wrap={false} style={S.trowWrap}>
                <View style={[S.tcell, { width: 110 }]}><Text style={S.td}>{g.marksNumbers}</Text></View>
                <View style={[S.tcell, { flex: 1 }]}><Text style={S.td}>{g.description || ''}</Text></View>
                <View style={[S.tcell, { width: 45 }]}><Text style={[S.tdBold, { textAlign: 'right' }]}>{g.nop ?? ''}</Text></View>
                <View style={[S.tcell, { width: 55 }]}><Text style={[S.td, { textAlign: 'right' }]}>{g.weight != null ? g.weight.toFixed(2).replace(/\.00$/, '') : ''}</Text></View>
                <View style={[S.tcell, { width: 45 }]}><Text style={[S.td, { textAlign: 'right' }]}>{g.cube != null ? g.cube.toFixed(2) : ''}</Text></View>
                <View style={[S.tcellLast, { width: 90 }]}><Text style={S.td}>{g.summary || ''}</Text></View>
              </View>
            ))}
            {/* Filler row: carries the column rules down to the bottom of the minHeight area. */}
            <View style={[S.trowWrap, { flexGrow: 1 }]}>
              <View style={[S.tcell, { width: 110 }]} />
              <View style={[S.tcell, { flex: 1 }]} />
              <View style={[S.tcell, { width: 45 }]} />
              <View style={[S.tcell, { width: 55 }]} />
              <View style={[S.tcell, { width: 45 }]} />
              <View style={[S.tcellLast, { width: 90 }]} />
            </View>
          </View>
        </View>

        {/* Remarks -- auto-filled Container/Seal (Sea) or AWB/MAWB (Air), see buildRemarks() */}
        <Text style={S.sectionLbl}>Remarks:</Text>
        <View style={S.remarksBox}>
          <Text style={S.remarksTxt}>{note.remarks || ''}</Text>
        </View>

        <Text style={S.sectionLbl}>Notes:</Text>
        <View style={S.notesBox}>
          <Text style={S.notesTxt}>{note.notes || ''}</Text>
        </View>

        {/* Authorisation -- top row (release-to name + Serial No), issuer signature row,
            "Received in good condition" header, blank receiver signature row. */}
        <View style={S.authBox}>
          <View style={S.authTopRow}>
            <Text style={S.authToTxt}>I AUTHORISE YOU TO RELEASE THESE GOODS TO: {note.authoriseTo ? note.authoriseTo.toUpperCase() : ''}</Text>
            <Text style={S.authSerialTxt}>Serial No: {note.serialNo ?? '(preview)'}</Text>
          </View>
          <View style={S.authValsRow}>
            <View style={S.authValCell}><Text style={S.authValTxt}> </Text></View>
            <View style={S.authValCell}><Text style={S.authValTxt}>{note.issuedByName || ''}</Text></View>
            <View style={S.authValCellLast}><Text style={S.authValTxt}>{fmtIssuedAt(note.issuedAt)}</Text></View>
          </View>
          <View style={S.authLblsRow}>
            <View style={S.authLblCell}><Text style={S.authLblTxt}>SIGNATURE</Text></View>
            <View style={S.authLblCell}><Text style={S.authLblTxt}>PRINT NAME</Text></View>
            <View style={S.authLblCellLast}><Text style={S.authLblTxt}>TIME/DATE</Text></View>
          </View>
          <Text style={S.authMidHdr}>RECEIVED IN GOOD CONDITION</Text>
          <View style={S.authValsRow}>
            <View style={S.authValCell}><Text style={S.authValTxt}> </Text></View>
            <View style={S.authValCell}><Text style={S.authValTxt}> </Text></View>
            <View style={S.authValCellLast}><Text style={S.authValTxt}> </Text></View>
          </View>
          <View style={[S.authLblsRow, { borderBottomWidth: 0 }]}>
            <View style={S.authLblCell}><Text style={S.authLblTxt}>SIGNATURE</Text></View>
            <View style={S.authLblCell}><Text style={S.authLblTxt}>PRINT NAME</Text></View>
            <View style={S.authLblCellLast}><Text style={S.authLblTxt}>TIME/DATE</Text></View>
          </View>
        </View>

        <View style={S.footer} fixed>
          {note.company.bifa_logo_url && <Image src={note.company.bifa_logo_url} style={S.bifaLogo} />}
          <Text style={S.footerTxt}>
            {companyRegistrationLine(note.company)}{'\n'}{note.company.bifa_line}
          </Text>
        </View>

      </Page>
    </Document>
  )
}
